import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { keys } from '../services/queryKeys'
import { cancelOrder, getOrder } from '../services/orderService'
import { PAYMENT_METHODS, labelForMethod } from '../utils/constants'
import { amountOwed } from '../utils/owed'
import JourneyRail from '../components/orders/JourneyRail'
import TrackingMap from '../components/specific/TrackingMap'
import RiderCard, { RIDER_ASSIGNED } from '../components/orders/RiderCard'
import { STATUS_JOURNEY, STATUS_LABELS } from '../components/orders/OrderStatus'
import Button from '../components/common/Button'
import CopyButton from '../components/common/CopyButton'
import { Empty, Failed, Loading } from '../components/common/States'
import { coords, dateTime, peso, phone, timeOnly } from '../utils/helpers'
import { DISPATCH_NOTE, MESSAGES } from '../utils/copy'
import fareFor from '../utils/fare'
import NotifyToggle from '../components/common/NotifyToggle'

/**
 * While these are the status, the delivery is still happening and the screen keeps
 * asking. `delivered` and `cancelled` are the two that end it.
 */
const OPEN_STATUSES = ['pending', 'accepted', 'picked_up']

const Row = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-4 py-2.5 border-b border-hairline last:border-b-0">
    <dt className="flex-none text-body-s text-ink-3">{label}</dt>
    <dd className="m-0 min-w-0 text-right text-body tabular-nums text-ink break-words">
      {children}
    </dd>
  </div>
)

/**
 * @param {string|null} shareToken When set, this is the shared view: the same order,
 *   opened by somebody holding the link rather than by the account that placed it.
 *   The page is identical — the owner's *controls* are not. See `OrderDetailRoute`.
 */
export default function OrderDetail({ shareToken = null }) {
  const { orderId } = useParams()
  const queryClient = useQueryClient()

  const shared = Boolean(shareToken)

  /**
   * Ten seconds, not the global thirty.
   *
   * This is the screen a customer leaves open while they wait, and the thing they are
   * waiting for — a rider accepting — changes underneath it without them doing anything.
   * Refetch-on-focus does the rest when they come back from another app.
   */
  const { isPending: loading, data, error } = useQuery({
    /**
     * The token is part of the key.
     *
     * Without it the shared view and the owner's view of the same order share a cache
     * entry — and they are not the same response. A customer who opens their own shared
     * link and then navigates to the order would be served whichever arrived first, and
     * on a finished order one of the two is a 410.
     */
    queryKey: shared ? [...keys.orders.detail(orderId), 'shared'] : keys.orders.detail(orderId),
    queryFn: () => getOrder(orderId, { shareToken }),
    staleTime: 10_000,
    /**
     * Thirty seconds while the delivery is running, and nothing once it has stopped.
     *
     * Until the map arrived this screen did not poll at all — refetch-on-focus was
     * enough, because the only thing that changed was a status word and a person coming
     * back to the tab would see it. A pin that does not move is a different failure: it
     * looks like the app, not like the rider.
     *
     * **Thirty, not ten**, and the number is the read budget rather than a feel. Each
     * poll is two Firestore reads — the order and the rider — so a half-hour delivery
     * watched by two people is ~120 apiece against a 50,000/day free tier. Fifteen would
     * double that for a pin that only moves every thirty seconds anyway, because that is
     * how often the rider app reports. See docs/intent/public-order-tracking.md.
     *
     * It stops the moment the order closes: a delivered order cannot change, and a phone
     * left on this tab should not keep waking up to be told so.
     */
    /**
     * Ten seconds once a rider is on the road, thirty while one is being found.
     *
     * The two states are watched for different things. During `pending` there is no rider
     * pin on the map at all — the only thing that can change is the dispatch note, and
     * asking three times a minute about a sentence is spending reads on nothing.
     *
     * From `accepted` the map has a rider moving on it, and that is what somebody has this
     * screen open to see. The server now writes their position roughly every forty metres
     * while they are carrying a delivery (`LOCATION_DEADBAND_ACTIVE_METRES`), so a
     * thirty-second poll was throwing most of those away — the pin jumped a block at a time
     * and looked stuck between jumps.
     *
     * ## What it costs
     *
     * A fifteen-minute delivery is 90 reads instead of 30. Firestore's free tier is 50,000
     * reads a day, and the tracking page is one document per poll — so this is worth
     * revisiting somewhere around **five hundred deliveries a day**, and not before.
     */
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status

      if (!OPEN_STATUSES.includes(status)) return false

      return RIDER_ASSIGNED.includes(status) ? 10_000 : 30_000
    },
    /**
     * A dead link is not a blip. 404 means this link never named an order and 410 means
     * the delivery is over; retrying either just delays the sentence that explains it.
     */
    retry: (count, failure) => ![404, 410].includes(failure?.status) && count < 2,
  })

  const [confirming, setConfirming] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [cancelError, setCancelError] = useState(null)

  const order = data?.data

  if (loading) return <Loading label="Loading order…" />

  /**
   * The link has finished its job. **410, not 404** — and the two say different things
   * on purpose: this one means the delivery happened, which is good news, and it must
   * not be dressed as an error. No action offered, because there is nowhere for somebody
   * without an account to go.
   */
  if (shared && error?.status === 410) {
    return (
      <Empty
        title="This delivery is finished"
        note="The tracking link has stopped working now that the order is complete. Thank you!"
      />
    )
  }

  if (error?.status === 404) {
    return (
      <Empty
        title={shared ? 'This link does not work' : 'Order not found'}
        note={
          shared
            ? 'It may be the wrong link, or the link may no longer be in use.'
            : 'It may have been placed on another account.'
        }
        // Nowhere to send a visitor with no account, so nothing is offered. "Back to my
        // orders" would be a sign-in wall dressed as a way out.
        action={
          shared ? null : (
            <Link
              to="/orders"
              className="text-body-s font-semibold text-brand underline underline-offset-4"
            >
              Back to my orders
            </Link>
          )
        }
      />
    )
  }

  if (error) return <Failed error={error} />

  const isCancelled = order.status === 'cancelled'
  const step = STATUS_JOURNEY.indexOf(order.status) + 1
  /**
   * `paymentType` decides this, not the method.
   *
   * It is the server's own two-way split and it is present on **every** order, including
   * the ones placed when there were eight methods to choose from. Looking the method up in
   * the local list would find nothing for an old `gcash` order and quietly treat it as not
   * cash — which on this screen is the difference between telling somebody to have money
   * ready and telling them it is already paid.
   */
  const paysCash = order.paymentType
    ? order.paymentType === 'cash'
    : PAYMENT_METHODS.find((method) => method.value === order.paymentMethod)?.type === 'cash'
  /**
   * Whether the delivery fee on this order is the final figure.
   *
   * It used to be `Boolean(order.rider)` — the fee was only knowable once somebody had
   * accepted, because the rider's own trip to the shop is part of it. That is no longer
   * true: an order placed through the summary was quoted before the customer committed,
   * and the whole fee was settled then.
   *
   * The server says which, because this screen cannot tell: `riderDistanceFee` arrives as
   * 0 when it was never priced *and* when it was priced at zero, and those mean opposite
   * things. The fallback covers a response from an older API.
   */
  const owed = amountOwed(order, paysCash)
  const feeSettled = owed.feeKnown

  /**
   * What is still owed, which is not the same as what the order cost.
   *
   * Four states — cash, paid-online with the fee outstanding, paid-online with the fee
   * included, and a fee nobody knows yet — and the screen used to show one number for all
   * of them. Lifted into `utils/owed.js` and tested there: it is the figure somebody reads
   * with their wallet in their hand, and it is not something to work out inside a render.
   */
  const { total, settled: settledInFull } = owed

  /**
   * What the map draws.
   *
   * The rider is included only while the order is still moving, and only when they have
   * actually reported — the server withholds `location` on a finished order, so this
   * follows it rather than deciding again. `TrackingMap` drops any pin without usable
   * coordinates, so a rider who has never reported costs the map its third pin and
   * nothing else.
   */
  const riderPoint = order.rider?.location ?? null

  // Null when there is no position or it carries no stamp, so the caption below can tell
  // "never reported" from "reported at 3:42" rather than printing a bare dash.
  const riderAsOf = riderPoint?.at ? timeOnly(riderPoint.at) : null

  const othersOnBoard = Number(order.rider?.otherDeliveries) || 0

  /**
   * Whether there is anything live left to say under the map.
   *
   * **Only while the delivery is running.** Every sentence in that block is about a thing
   * still in motion — where the rider is *now*, who else they are carrying, what we are
   * waiting for. On a finished order all three are false, and the first draft of this
   * proved it: a delivered order sat there saying "Waiting for your rider to report their
   * location", which is not waiting for anything.
   *
   * A closed order keeps its map — the two ends of a journey that happened are worth
   * seeing — and says nothing under it.
   */
  const liveCaption = OPEN_STATUSES.includes(order.status)

  /**
   * The link the owner passes on.
   *
   * Built from `trackToken`, which the server puts on this payload and on no other — see
   * `OrderService.present`. Absolute, because it is going into a chat message where a
   * relative path means nothing.
   *
   * Null once the order closes, which is what hides the button: the same close that makes
   * the server answer 410. One rule, expressed once.
   */
  const shareUrl = !shared && order.trackToken && OPEN_STATUSES.includes(order.status)
    ? `${window.location.origin}/orders/${encodeURIComponent(order.orderId)}?t=${encodeURIComponent(order.trackToken)}`
    : null

  const mapPins = [
    { key: 'pickup', variant: 'pickup', label: order.pickupSource || 'Pickup', lat: order.pickupLatitude, lng: order.pickupLongitude },
    { key: 'dropoff', variant: 'dropoff', label: order.dropoffAddress || 'Drop-off', lat: order.dropoffLatitude, lng: order.dropoffLongitude },
    ...(riderPoint
      ? [{ key: 'rider', variant: 'rider', label: order.rider.displayName || 'Your rider', lat: riderPoint.latitude, lng: riderPoint.longitude }]
      : []),
  ].filter((pin) => Number.isFinite(Number(pin.lat)) && Number.isFinite(Number(pin.lng)))

  // Recomputed from the partner's rules rather than stored as a sentence, so the
  // words can never describe a fee the formula did not produce.
  const rules = order.partner.pricing
  const fare = rules ? fareFor(order.distanceKm, rules) : null

  const feeNote =
    !fare || fare.refused
      ? null
      : fare.chargeableKm > 0
        ? `${peso(rules.baseFare)} base plus ${peso(fare.distanceCharge)} for the ${fare.chargeableKm} km past the ${rules.kmIncludedInBase} km it covers.`
        : fare.liftedToMinimum
          ? `${order.partner.businessName}'s minimum fare. Their base covers the first ${rules.kmIncludedInBase} km and you are inside it.`
          : `${peso(rules.baseFare)} base fare — the first ${rules.kmIncludedInBase} km are included, and you are inside that.`

  const onConfirmCancel = async () => {
    setCancelling(true)
    setCancelError(null)

    try {
      await cancelOrder(order.orderId)
      setConfirming(false)

      // The cancel refunds a commission if one was taken, so more than this screen moved.
      // Invalidating by prefix refetches the detail and the list together rather than
      // patching a local copy that then drifts from the server.
      await queryClient.invalidateQueries({ queryKey: keys.orders.all })
    } catch (failure) {
      setCancelError(failure)
    } finally {
      setCancelling(false)
    }
  }

  return (
    <article className="px-5 pt-4 pb-7">
      {/* "My orders" is not this visitor's orders, and the link is a sign-in wall for
          anybody who did not place this one. */}
      {!shared && (
        <Link
          to="/orders"
          className="inline-flex items-center min-h-[32px] text-body-s text-ink-3 transition-colors duration-quick hover:text-brand"
        >
          ← My orders
        </Link>
      )}

      {/* The hero, because this is the thing a customer screenshots and sends
          over Messenger. */}
      <p className="mt-3.5 text-label font-semibold uppercase text-ink-3">Order number</p>
      <h1 className="mt-1 font-display font-bold text-display-xl tabular-nums text-ink">
        {order.orderId}
      </h1>
      <p className="mt-1 text-body text-ink-2">{order.partner.businessName}</p>

      {/**
        * Sharing, offered at the moment somebody is already thinking about it.
        *
        * The order number above is described in this file as "the thing a customer
        * screenshots and sends over Messenger" — so this sits directly under it, because
        * that screenshot is what it replaces. A picture of a status goes stale the second
        * it is taken; this link keeps answering.
        *
        * Only for the person who placed the order (`!shared`), and only while the
        * delivery is running — the link 410s once it is finished, and a button that
        * copies a dead link is worse than no button.
        */}
      {!shared && shareUrl && (
        <div className="mt-4 border border-hairline rounded-card bg-surface px-4 py-3.5">
          <div className="flex items-center justify-between gap-3">
            <p className="text-body-s font-semibold text-ink">Waiting for this at home?</p>
            <CopyButton text={shareUrl} label="Copy link" size="sm" className="flex-none" />
          </div>

          {/* Said plainly rather than buried, because it is the part that surprises
              people: the link needs no account, so it works for whoever it reaches —
              and it keeps working until the delivery is done. */}
          <p className="mt-1.5 text-body-s text-ink-3">
            Send this to whoever is waiting. They can follow the rider without signing
            in, and the link stops working once the order arrives.
          </p>
        </div>
      )}

      <div className="mt-5 border border-hairline rounded-card px-4 py-[18px] bg-surface">
        <div className="flex items-center justify-between gap-3 mb-4">
          <span
            className={`text-label font-semibold uppercase ${
              isCancelled ? 'text-alert' : 'text-brand'
            }`}
          >
            {STATUS_LABELS[order.status] ?? order.status}
          </span>
          <span className="text-body-s text-ink-3">
            {isCancelled ? 'Left the journey' : `Step ${step} of ${STATUS_JOURNEY.length}`}
          </span>
        </div>

        {/* `pending` is one word covering five different situations, and it can sit there
            for minutes. The rail fills the same 4% whether a rider is being asked right
            now or every rider has already refused — so the difference is said in words.
            `exhausted` is the one worth acting on.

            The cancel line is added here rather than carried in the note itself: the
            shared view renders the same note and has no cancel button to point at. */}
        {order.status === 'pending' && DISPATCH_NOTE[order.dispatchState] && (
          <p
            className={`-mt-1 mb-4 text-body-s ${
              order.dispatchState === 'exhausted' ? 'text-warn' : 'text-ink-2'
            }`}
          >
            {DISPATCH_NOTE[order.dispatchState]}
            {!shared && order.dispatchState === 'exhausted' && ` ${MESSAGES.canStillCancel}`}
          </p>
        )}

        {/* Offered where a customer is already waiting for something to change, which is
            the one moment being notified is obviously worth it. Only while the order is
            still moving — asking to notify somebody about a delivered order is noise. */}
        {/* Not on the shared view: subscribing binds a push token to *this account*, and
            the person reading a forwarded link has none to bind it to. */}
        {!shared && !isCancelled && order.status !== 'delivered' && (
          <NotifyToggle label="Notify me when this changes" />
        )}

        <JourneyRail
          status={order.status}
          pickup={order.pickupSource}
          pickupPin={coords(order.pickupLatitude, order.pickupLongitude)}
          /* The address the customer chose, now that orders carry one. It was hardcoded
             as "A dropped pin" because there was nothing else to show. */
          dropoff={order.dropoffAddress ?? 'A dropped pin'}
          dropoffPin={coords(order.dropoffLatitude, order.dropoffLongitude)}
        />
      </div>

      {/* The two ends of the journey, and the rider between them once there is one.
          Below the rail rather than above it, because the rail is the thing that answers
          "has it been picked up yet" and the map is the thing that answers "where". */}
      {mapPins.length > 0 && (
        <>
          <TrackingMap
            pins={mapPins}
            className={`mt-4 overflow-hidden border border-hairline ${
              liveCaption ? 'rounded-t-card border-b-0' : 'rounded-card'
            }`}
          />

          {/* The caption belongs under the map rather than in a tooltip on the pin: this
              is read on a phone, where there is no hover, and both of these sentences are
              the ones somebody actually came here for. */}
          {liveCaption && (
          <div className="rounded-b-card border border-hairline bg-surface px-4 py-3">
            {/**
              * **When the pin was last true**, which is not the same as now.
              *
              * The rider app reports every thirty seconds with the app open, but only
              * every three minutes with the phone pocketed and the rider standing still —
              * a limit of not touching `MachiExpress`, recorded in the intent doc. Without
              * this line a pin that has not moved for three minutes reads as a broken app.
              * With it, it reads as a rider who was there at 3:42 and has not been heard
              * from since, which is the truth.
              */}
            {riderAsOf ? (
              <p className="text-body-s text-ink-3">
                Rider&apos;s location as of {riderAsOf}
              </p>
            ) : (
              <p className="text-body-s text-ink-3">
                {order.rider
                  ? 'Waiting for your rider to report their location.'
                  : 'Your rider will appear here once one accepts.'}
              </p>
            )}

            {/**
              * Why the rider may be heading away from you.
              *
              * **Not a queue position.** Nothing in the data knows whose parcel is
              * dropped first, so this says what is true — they are carrying another one —
              * and does not claim an order it cannot see.
              */}
            {othersOnBoard > 0 && (
              <p className="mt-1 text-body-s text-ink-2">
                Your rider has {othersOnBoard} other {othersOnBoard === 1 ? 'delivery' : 'deliveries'} right now.
              </p>
            )}
          </div>
          )}
        </>
      )}

      {/* Only once somebody has actually been assigned. Both conditions are
          checked: a rider that arrives on a cancelled order is bad data, not
          something to render. */}
      {order.rider && RIDER_ASSIGNED.includes(order.status) && (
        <RiderCard rider={order.rider} status={order.status} />
      )}

      <div className="mt-4 border border-hairline rounded-card overflow-hidden">
        <p className="px-4 py-3 text-label font-semibold uppercase text-ink-3 bg-surface-2 border-b border-hairline">
          The order
        </p>

        <dl className="m-0 px-4 pt-1 pb-3 bg-surface">
          <Row label="What">{order.itemDetails.description}</Row>

          {order.itemDetails.items.map((item, index) => (
            <Row key={index} label={`${item.quantity}× ${item.name}`}>
              {peso(item.estimatedPrice)}
            </Row>
          ))}

          <Row label="Items">{order.itemDetails.totalItems}</Row>
          {order.pickupTime && <Row label="Pickup time">{dateTime(order.pickupTime)}</Row>}
          {order.deliveryTime && <Row label="Delivery time">{dateTime(order.deliveryTime)}</Row>}
          <Row label="Rider calls">{phone(order.contact.phoneNumber)}</Row>
          <Row label="Note">{order.specialInstructions ?? '—'}</Row>
        </dl>
      </div>

      {/* Money gets its own block rather than three more rows above, because
          there are two different pots here and the difference is the whole point.
          The goods are an estimate — the rider buys them and the shop's price is
          the shop's price, which is why a maximum exists. The delivery fee is
          fixed and known now. A customer answering the door needs both, and
          needs to know which one can move. */}
      <div className="mt-4 border border-hairline rounded-card overflow-hidden">
        <p className="px-4 py-3 text-label font-semibold uppercase text-ink-3 bg-surface-2 border-b border-hairline">
          What you pay
        </p>

        <dl className="m-0 px-4 pt-1 pb-1 bg-surface">
          <Row label="Goods (estimate)">{peso(order.estimatedTotal)}</Row>
          <Row label="Your maximum">{peso(order.maxBudget)}</Row>
          {/**
            * Before a rider accepts this said "Once a rider accepts" and nothing else,
            * which is honest and useless — the customer is deciding whether to book and
            * has been told nothing about the price.
            *
            * Most of it *is* knowable: the base and the distance come from the partner's
            * own rules, which this screen already has. The only unknown is the rider's
            * trip to the shop, and that has a ceiling. Quoting the known part with its
            * worst case named is the difference between a price that went up and a price
            * they were told about.
            */}
          <Row label="Delivery fee">
            {/* One number or none. The "+ up to ₱60" that used to sit here was the app
                admitting it did not know — and now it does: the quote settles the rider's
                trip before the order is placed. Naming a ceiling beside a fee the customer
                has already agreed to is two different answers to the same question. */}
            {feeSettled ? peso(order.deliveryFee) : 'Once a rider accepts'}
          </Row>
        </dl>

        {/* The fee explained in the partner's own arithmetic, word for word the
            way their console explains it. A fee nobody can check is a fee
            somebody messages about. */}
        {feeSettled && feeNote && <p className="px-4 pb-3 bg-surface text-body-s text-ink-3">{feeNote}</p>}

        {/* The same arithmetic, before anybody has accepted — plus the one part that is
            still open, named with its ceiling rather than left as a surprise. */}
        {!feeSettled && feeNote && (
          <p className="px-4 pb-3 bg-surface text-body-s text-ink-3">
            {feeNote}
            {` The rider's own trip to ${order.pickupSource || 'the pickup'} is added when one accepts.`}
          </p>
        )}

        {/* Why the fee is higher than the shop's own rates would suggest.
            The rider's trip *to the shop* is the reason the fee could not be
            quoted earlier — it depends on which rider took it — so it is named
            rather than folded silently into a bigger number. A charge that
            appears with no explanation is the one argued about at the door,
            and the rider is the one standing there. */}
        {feeSettled && order.riderDistanceFee > 0 && (
          <p className="px-4 pb-3 bg-surface text-body-s text-ink-3">
            Includes {peso(order.riderDistanceFee)} because your rider came
            {order.distanceFeeRule?.chargeableKm
              ? ` ${order.distanceFeeRule.chargeableKm} km`
              : ' a distance'}{' '}
            to reach the shop. That is why the fee was only shown once they accepted.
          </p>
        )}

        <div className="px-4 py-4 bg-blush border-t border-hairline">
          {/* Named for what the customer has to *do*, which is different in all four
              cases. "Total" over a zero is the sentence somebody argues with while
              holding their wallet. */}
          <p className="text-label font-semibold uppercase text-ink-2">
            {isCancelled
              ? 'Nothing to pay'
              : settledInFull
                ? 'Already paid'
                : paysCash
                  ? 'Have ready'
                  : 'Still to pay'}
          </p>

          {isCancelled ? (
            <p className="mt-1 text-body text-ink-2">
              This order was cancelled, so neither the goods nor the delivery were charged.
            </p>
          ) : settledInFull ? (
            <>
              {/* Exact, and said as a number rather than only in words. "You owe nothing"
                  is reassuring; ₱0.00 beside it is checkable. */}
              <p className="mt-1 text-display-m font-bold tabular-nums text-ink">{peso(0)}</p>
              <p className="mt-1 text-body-s text-ink-2">
                {peso(order.estimatedTotal)} for the goods and {peso(order.deliveryFee)} for the ride
                were both settled online. Hand the rider nothing.
              </p>
            </>
          ) : (
            <>
              {/* display-m as a size, not as the display face — this is a number
                  in DM Sans, and Playfair stays on the order number above. */}
              <p className="mt-1 text-display-m font-bold tabular-nums text-ink">
                {/* "about" only where the figure can still move — which is the goods, and
                    only on a cash order. A fee that is settled is not an estimate. */}
                {paysCash ? `about ${peso(total)}` : peso(total)}
              </p>

              <p className="mt-1 text-body-s text-ink-2">
                {paysCash
                  ? (feeSettled
                    ? `${peso(order.estimatedTotal)} for the goods, ${peso(order.deliveryFee)} for the ride. `
                    : `${peso(order.estimatedTotal)} for the goods. The ride is added once a rider accepts. `)
                  : `The goods were settled online. This is the ride only. `}
                {paysCash && `You pay what the goods actually cost, up to your ${peso(order.maxBudget)} maximum, so this can move.`}
              </p>

              <p className="mt-1.5 text-body-s text-ink-3">
                {paysCash
                  ? 'Cash, straight to the rider.'
                  : `Paid with ${labelForMethod(order.paymentMethod)}. The rider collects the delivery fee.`}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Cancelling is the customer's decision and nobody else's. The server refuses it
          from a tokened caller regardless — there is no session to own the order — but a
          button that exists only to fail is not something to show a person waiting for
          their delivery. */}
      {!shared && order.status === 'pending' && (
        <div className="mt-4">
          {confirming ? (
            <div className="border border-alert rounded-card p-4 bg-surface">
              <p className="text-body font-semibold text-ink">Cancel this order?</p>
              <p className="mt-1.5 text-body-s text-ink-2">
                No rider has accepted it yet, so nothing is on the road. This cannot be undone.
              </p>

              {cancelError && (
                <p role="alert" className="mt-2 text-body-s text-alert">
                  {cancelError.message}
                </p>
              )}

              <div className="flex gap-2.5 mt-3.5">
                <Button
                  variant="ghost"
                  size="compact"
                  onClick={() => setConfirming(false)}
                  disabled={cancelling}
                  className="flex-1"
                >
                  Keep it
                </Button>
                <Button
                  variant="destructive"
                  size="compact"
                  onClick={onConfirmCancel}
                  disabled={cancelling}
                  className="flex-1"
                >
                  {cancelling ? 'Cancelling…' : 'Yes, cancel'}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Button
                variant="danger"
                size="compact"
                onClick={() => setConfirming(true)}
                className="w-full"
              >
                Cancel this order
              </Button>
              <p className="mt-2 text-body-s text-ink-3 text-center">
                You can cancel until a rider accepts.
              </p>
            </>
          )}
        </div>
      )}

      <p className="mt-4 text-body-s text-ink-3">Placed {dateTime(order.createdAt)}</p>
    </article>
  )
}
